/**
 * Shared rendering parts for condition renderers, built on Pluma's
 * ConditionViewer primitives.
 */

import {
  ConditionViewerDetail,
  ConditionViewerDetailList,
  ConditionViewerTerm,
} from '@customerio/pluma-components/react';
import plur from 'plur';
import { formatWithin } from '../parse/parse-helpers';
import type { ConditionFilter } from '../types';

type TimesProps = { times?: number };

export function Times({ times }: TimesProps) {
  if (times == null || times <= 1) return null;
  return (
    <>
      {' '}
      at least <ConditionViewerTerm>{times}</ConditionViewerTerm> {plur('time', times)}
    </>
  );
}

type WithinProps = { within?: number };

export function Within({ within }: WithinProps) {
  if (within == null || within === 0) return null;
  return (
    <>
      {' '}
      within <ConditionViewerTerm>{formatWithin(within)}</ConditionViewerTerm>
    </>
  );
}

type MaskedValueProps = {
  value: string;
};

/**
 * A value a condition compares against — subscriber data, unlike the field name
 * beside it. The viewer renders inside a segment hover popover that portals to
 * document.body, so it cannot inherit an ancestor's fs-mask; each value masks
 * itself to stay out of FullStory session replay (FPS-3731).
 */
function MaskedValue({ value }: MaskedValueProps) {
  return <ConditionViewerTerm className="fs-mask">{value}</ConditionViewerTerm>;
}

type FieldOperatorValueProps = {
  field?: string;
  operator?: string;
  /** What the value names when it is another attribute, read between the operator and the value. */
  valueSource?: string;
  value?: string;
  value2?: string;
};

export function FieldOperatorValue({ field, operator, valueSource, value, value2 }: FieldOperatorValueProps) {
  return (
    <>
      {field != null ? <ConditionViewerTerm>{field}</ConditionViewerTerm> : null}
      {operator != null ? <> {operator}</> : null}
      {valueSource != null ? <> {valueSource}</> : null}
      {value != null ? (
        <>
          {' '}
          <MaskedValue value={value} />
        </>
      ) : null}
      {value2 != null ? (
        <>
          {' '}
          and <MaskedValue value={value2} />
        </>
      ) : null}
    </>
  );
}

export function hasFilters(filters: ConditionFilter[] | undefined): filters is ConditionFilter[] {
  return filters != null && filters.length > 0;
}

/**
 * The lead-in phrase for a filter list, e.g. "and matches all object attributes:".
 * Pluma wants this at the end of the item's sentence (first list) or as the
 * detail list's `label` (second and subsequent lists).
 */
export function filtersLeadInText(filtersOperator?: 'and' | 'or', label?: string): string {
  const operatorLabel = filtersOperator === 'or' ? 'any' : 'all';
  return `and matches ${operatorLabel}${label != null ? ` ${label}` : ''}:`;
}

type FiltersLeadInProps = {
  filters?: ConditionFilter[];
  filtersOperator?: 'and' | 'or';
  /** Optional type label shown after the operator (e.g. "object attributes"). */
  label?: string;
};

/** Sentence suffix for an item whose filters render in its `details`. */
export function FiltersLeadIn({ filters, filtersOperator, label }: FiltersLeadInProps) {
  if (!hasFilters(filters)) return null;
  return <> {filtersLeadInText(filtersOperator, label)}</>;
}

type FilterDetailListProps = {
  filters?: ConditionFilter[];
  /** Lead-in for the second and subsequent lists on an item; the first list's lead-in belongs in the sentence. */
  label?: string;
};

/** Filter chips for an item's `details` area. */
export function FilterDetailList({ filters, label }: FilterDetailListProps) {
  if (!hasFilters(filters)) return null;
  return (
    <ConditionViewerDetailList label={label}>
      {filters.map((filter, index) => (
        <ConditionViewerDetail key={`${index}-${filter.field}-${filter.operator}`}>
          <FieldOperatorValue
            field={filter.field}
            operator={filter.operator}
            value={filter.value}
            value2={filter.value2}
          />
        </ConditionViewerDetail>
      ))}
    </ConditionViewerDetailList>
  );
}
